import { useEffect, useState } from "react";

import { alpha, Box } from "@mui/material";
import Button from "@mui/material/Button";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { Form, Formik, FormikHelpers } from "formik";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { login } from "../../../../common/services/auth/Auth.service";
import { CfErrorPage } from "../../../../shared/components/common/CfErrorPage/CfErrorPage";
import { FormPasswordInput } from "../../../../shared/components/form/FormPasswordInput/FormPasswordInput";
import { FormTextField } from "../../../../shared/components/form/FormTextField/FormTextField";
import { EntryContainer } from "../../../../shared/containers/EntryContainer/EntryContainer";
import { LoginLinks } from "../../components/LoginLinks/LoginLinks";

import { HasLogged } from "./HasLogged";

const Login = () => {
  const navigate = useNavigate();
  const classes = useStyles();

  const [loginError, setLoginError] = useState<React.ReactNode>();
  const [hasLogged, setHasLogged] = useState(false);
  const [error, setError] = useState({});

  useEffect(() => {
    if (hasLogged) {
      setHasLogged(false);
    }
  }, [hasLogged]);

  const initialValues = {
    username: "",
    password: "",
  };

  const onLogin = async (
    values: typeof initialValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>,
  ) => {
    const { password, username } = values;
    setLoginError(undefined);

    return login({ username, password })
      .then(() => {
        setHasLogged(true);
        navigate("/farm");
      })
      .catch((err: Record<string, number>) => {
        const status = err && err.status ? err.status : null;
        switch (status) {
          case 400:
          case 401:
            setLoginError(<FormattedMessage id="Login.incorrect-login" />);
            break;
          case 500:
          case 503:
            setError({
              isError: true,
              code: err.status,
            });
            break;
          default:
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const validate = (values: typeof initialValues) => {
    const errors: { [key in keyof typeof values]?: React.ReactNode } = {};
    if (!values?.username) {
      errors.username = <FormattedMessage id="Login.field-is-required" />;
    }
    if (!values?.password) {
      errors.password = <FormattedMessage id="Login.field-is-required" />;
    }
    return errors;
  };

  return (
    <CfErrorPage error={error}>
      <HasLogged hasLogged={hasLogged} />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onLogin}
        validate={validate}
      >
        {({ isSubmitting }) => (
          <EntryContainer isFetching={isSubmitting}>
            <Form autoComplete="off">
              <Box
                className={classes.loginContainer}
                display="flex"
                flexDirection="column"
                gap={2}
              >
                <Box>
                  <FormTextField
                    autoFocus={true}
                    className={classes.textField}
                    id="username"
                    label={<FormattedMessage id="Login.username" />}
                    margin="dense"
                    name="username"
                    type="text"
                    InputLabelProps={{
                      className: classes.inputLabel,
                    }}
                  />
                </Box>
                <FormPasswordInput
                  fullWidth={true}
                  label={<FormattedMessage id="Login.password" />}
                  name="password"
                />
                <Button
                  className={classes.button}
                  color="primary"
                  id="login"
                  type="submit"
                  variant="contained"
                >
                  <FormattedMessage id="Login.log-in" />
                </Button>
                {loginError && (
                  <div
                    className={classes.loginError}
                    id="incorrect-login-alert"
                  >
                    <p>{loginError}</p>
                  </div>
                )}
                <LoginLinks />
              </Box>
            </Form>
          </EntryContainer>
        )}
      </Formik>
    </CfErrorPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    textAlign: "center",
    backgroundColor: alpha(theme.palette.common.white, 0.8),
    padding: 30,
    position: "relative",
  },
  textField: {
    textAlign: "left",
    width: "100%",
    marginTop: 0,
  },
  errorText: {
    position: "absolute",
    bottom: -10,
  },
  floatingLabelStyle: {
    fontWeight: "normal",
    fontSize: 14,
  },
  inputLabel: {
    fontWeight: 400,
    zIndex: 1,
    pointerEvents: "none",
  },
  button: {
    width: "100%",
  },
  loginErrorHolder: {
    marginBottom: 10,
    position: "relative",
  },
  loginError: {
    position: "absolute",
    top: 0,
    left: 0,
    fontWeight: "normal",
    fontSize: 14,
    textAlign: "center",
    width: "100%",
    color: theme.palette.error.main,
  },
  loginContainer: {
    width: "100%",
    margin: "40px 0px 10px 0px",
  },
}));

export { Login };
